// flash.geom equivalents, trimmed to the surface the port touches.

export class Point {
  constructor(
    public x = 0,
    public y = 0,
  ) {}
}

export class Rectangle {
  constructor(
    public x = 0,
    public y = 0,
    public width = 0,
    public height = 0,
  ) {}

  get right(): number {
    return this.x + this.width;
  }

  get bottom(): number {
    return this.y + this.height;
  }

  clone(): Rectangle {
    return new Rectangle(this.x, this.y, this.width, this.height);
  }

  setTo(x: number, y: number, width: number, height: number): void {
    this.x = x;
    this.y = y;
    this.width = width;
    this.height = height;
  }
}

/** Flash 2x3 affine matrix: [a c tx / b d ty]. */
export class Matrix {
  constructor(
    public a = 1,
    public b = 0,
    public c = 0,
    public d = 1,
    public tx = 0,
    public ty = 0,
  ) {}

  clone(): Matrix {
    return new Matrix(this.a, this.b, this.c, this.d, this.tx, this.ty);
  }

  identity(): void {
    this.a = 1;
    this.b = 0;
    this.c = 0;
    this.d = 1;
    this.tx = 0;
    this.ty = 0;
  }

  /** this = this * m (m applied after this, matching flash concat). */
  concat(m: Matrix): void {
    const { a, b, c, d, tx, ty } = this;
    this.a = a * m.a + b * m.c;
    this.b = a * m.b + b * m.d;
    this.c = c * m.a + d * m.c;
    this.d = c * m.b + d * m.d;
    this.tx = tx * m.a + ty * m.c + m.tx;
    this.ty = tx * m.b + ty * m.d + m.ty;
  }

  invert(): void {
    const det = this.a * this.d - this.b * this.c;
    if (det === 0) {
      this.identity();
      return;
    }
    const { a, b, c, d, tx, ty } = this;
    this.a = d / det;
    this.b = -b / det;
    this.c = -c / det;
    this.d = a / det;
    this.tx = (c * ty - d * tx) / det;
    this.ty = (b * tx - a * ty) / det;
  }
}

export class ColorTransform {
  constructor(
    public redMultiplier = 1,
    public greenMultiplier = 1,
    public blueMultiplier = 1,
    public alphaMultiplier = 1,
    public redOffset = 0,
    public greenOffset = 0,
    public blueOffset = 0,
    public alphaOffset = 0,
  ) {}

  get isIdentity(): boolean {
    return (
      this.redMultiplier === 1 &&
      this.greenMultiplier === 1 &&
      this.blueMultiplier === 1 &&
      this.alphaMultiplier === 1 &&
      this.redOffset === 0 &&
      this.greenOffset === 0 &&
      this.blueOffset === 0 &&
      this.alphaOffset === 0
    );
  }

  clone(): ColorTransform {
    return new ColorTransform(
      this.redMultiplier,
      this.greenMultiplier,
      this.blueMultiplier,
      this.alphaMultiplier,
      this.redOffset,
      this.greenOffset,
      this.blueOffset,
      this.alphaOffset,
    );
  }

  concat(second: ColorTransform): void {
    this.redOffset += this.redMultiplier * second.redOffset;
    this.greenOffset += this.greenMultiplier * second.greenOffset;
    this.blueOffset += this.blueMultiplier * second.blueOffset;
    this.alphaOffset += this.alphaMultiplier * second.alphaOffset;
    this.redMultiplier *= second.redMultiplier;
    this.greenMultiplier *= second.greenMultiplier;
    this.blueMultiplier *= second.blueMultiplier;
    this.alphaMultiplier *= second.alphaMultiplier;
  }
}
